import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { ProForm } from 'components/layout';
import AntdProForm, {
  ProFormText,
  ProFormDependency,
  ProFormDigit,
  ProFormTextArea,
  ProFormDatePicker,
  ProFormRadio,
} from '@ant-design/pro-form';
import { Statistic, Button, Divider } from 'antd';
import { FormUpload } from 'components/input';
import dateDiffInMonthsAndDays from 'utils/dateDiffInMonthsAndDays';
import TeamMemberList from './TeamMemberList';

const ProjectApplicationForm = ({
  initialValues,
  onFinish,
  onSave,
  loading,
  disabled,
  ...rest
}) => {
  const [form] = AntdProForm.useForm();

  return (
    <ProForm
      loading={loading}
      form={form}
      requiredMark={false}
      initialValues={initialValues}
      onFinish={onFinish}
      submitter={{
        render: (_, doms) =>
          disabled
            ? [doms[0]]
            : [
                doms[1],
                onSave && (
                  <Button
                    type="primary"
                    key="save"
                    onClick={async () => await onSave(form)}
                  >
                    Resume Later
                  </Button>
                ),
                doms[0],
              ],
        searchConfig: {
          resetText: 'Back',
        },
      }}
      {...rest}
    >
      <ProFormText
        width="xxl"
        name="refNumber"
        label="Application ref. number"
        disabled
        rules={[{ required: false }]}
      />
      <ProFormText
        width="xxl"
        name="title"
        label="Project title"
        disabled={disabled}
        rules={[{ required: true, message: 'Please enter project title' }]}
      />
      <ProFormRadio.Group
        label="Is the project research-oriented or with research elements?"
        tooltip="The research elements could be either basic research or applied research, and the deliverables usually include research outcomes, such as technological applications, scientific papers, etc."
        name="isResearchOriented"
        options={[
          {
            label: 'Yes',
            value: true,
          },
          {
            label: 'No',
            value: false,
          },
        ]}
        disabled={disabled}
        rules={[{ required: true, message: 'Please select' }]}
      />
      <TeamMemberList disabled={disabled} />
      <Divider />
      <ProFormDependency name={['tdgFund', 'matchingFund']}>
        {({ tdgFund, matchingFund }) => (
          <div style={{ marginBottom: '16px' }}>
            <Statistic
              title="Total funding requested"
              value={
                (tdgFund > 0 ? tdgFund : 0) +
                (matchingFund > 0 ? matchingFund : 0)
              }
              prefix="$"
              precision={2}
            />
          </div>
        )}
      </ProFormDependency>
      <ProFormDigit
        label="From TDG"
        name="tdgFund"
        width="md"
        min={0}
        fieldProps={{
          formatter: (value) => `$ ${value}`,
          precision: 2,
        }}
        disabled={disabled}
        rules={[
          {
            required: true,
          },
          {
            type: 'number',
            min: 0.01,
            message: 'Funding from TDG must be greater than 0',
          },
        ]}
      />
      <ProFormDigit
        label="From matching fund"
        name="matchingFund"
        width="md"
        min={0}
        disabled={disabled}
        fieldProps={{
          formatter: (value) => `$ ${value}`,
          precision: 2,
        }}
        rules={[
          {
            required: true,
          },
        ]}
        tooltip={
          <span>
            <b>
              <i>Matching fund</i>
            </b>{' '}
            here refers to the amount of funding that the host
            department/faculty will contribute to the overall budget of the
            project.
          </span>
        }
      />
      <ProFormTextArea
        name="matchingFundFundingSource"
        label="Funding source of matching fund"
        width="xl"
        disabled={disabled}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value && getFieldValue('matchingFund') > 0) {
                return Promise.reject(
                  new Error(
                    'Matching fund is greater than 0, please enter funding source of matching fund'
                  )
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
      />
      <Divider />
      <ProFormDependency name={['startDate', 'endDate']}>
        {({ startDate, endDate }) => {
          return (
            <div style={{ marginBottom: '16px' }}>
              <Statistic
                title="Expected duration of project"
                value={dateDiffInMonthsAndDays(startDate, endDate)}
                formatter=""
              />
            </div>
          );
        }}
      </ProFormDependency>
      <ProFormDatePicker
        width="xl"
        name="startDate"
        label="Proposed commencement date"
        placeholder="YYYY-MM-DD"
        disabled={disabled}
        rules={[
          { required: true },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const endDate = moment(getFieldValue('endDate')).startOf('day');
              if (!value || !endDate) return Promise.resolve();
              const startDate = moment(value).startOf('day');
              const today = moment().startOf('day');
              if (!startDate.isAfter(today))
                return Promise.reject(
                  new Error('Commencement date must be after today')
                );

              if (endDate.isSameOrBefore(startDate)) {
                return Promise.reject(
                  new Error(
                    'Commencement date must be before expected completion date'
                  )
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
      />
      <ProFormDatePicker
        width="xl"
        name="endDate"
        label="Expected completion date"
        placeholder="YYYY-MM-DD"
        disabled={disabled}
        rules={[{ required: true }]}
        onChange={() => {
          form.validateFields(['startDate']);
        }}
      />
      <Divider />
      <FormUpload
        maxCount={1}
        name="proposal"
        label="Proposal"
        disabled={disabled}
        rules={[{ required: true, message: 'Please upload file!' }]}
        projectId="PROJECT_APPLICATION"
        subFolder={`fundingId/applicationId`}
      />
    </ProForm>
  );
};

ProjectApplicationForm.defaultProps = {
  initialValues: {},
  loading: true,
  disabled: false,
};

ProjectApplicationForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  onFinish: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  loading: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
};

export default ProjectApplicationForm;
